// URL ↔ store sync — managed entirely by Zustand outside React.
//
// Mirrors the relevant slices of viewStore (selectedMachines, linkedPins) and
// historyStore (range, customRange) to/from the URL. No React effect involved
// → no render loop hazard.

import { useViewStore } from './viewStore';
import { useHistoryStore } from './dashboardStore';
import { MAX_HISTORY_WINDOW_MS, type TimeRange } from '@/types';

// Allow-list for URL-parsed ranges. '7d' is valid again (issue #15).
const VALID_RANGES: ReadonlyArray<TimeRange> = ['live', '1h', '6h', '24h', '72h', '7d', 'custom'];
function coerceRange(raw: string | null): TimeRange | null {
  if (!raw) return null;
  return (VALID_RANGES as readonly string[]).includes(raw) ? (raw as TimeRange) : null;
}

const KEYS = ['machines', 'pins', 'range', 'from', 'to'] as const;

function buildSearch(): string {
  const v = useViewStore.getState();
  const h = useHistoryStore.getState();
  const next = new URLSearchParams(window.location.search);
  KEYS.forEach((k) => next.delete(k));
  if (v.selectedMachines.length) next.set('machines', v.selectedMachines.join(','));
  if (v.linkedPins.length)       next.set('pins',     v.linkedPins.join(','));
  if (h.range !== 'live')        next.set('range',    h.range);
  if (h.range === 'custom' && h.customRange) {
    next.set('from', String(h.customRange.fromMs));
    next.set('to',   String(h.customRange.toMs));
  }
  return next.toString();
}

function writeUrl(): void {
  if (!window.location.pathname.startsWith('/history')) return;
  const next = buildSearch();
  const cur  = window.location.search.replace(/^\?/, '');
  if (cur === next) return;
  const url = `${window.location.pathname}${next ? '?' + next : ''}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', url);
}

let installed = false;

export function setupHistoryUrlSync(): void {
  if (installed) return;
  installed = true;
  // Subscribe to both stores. Any change in the relevant slice → re-write URL.
  useViewStore.subscribe((state, prev) => {
    if (state.selectedMachines === prev.selectedMachines && state.linkedPins === prev.linkedPins) return;
    writeUrl();
  });
  useHistoryStore.subscribe((state, prev) => {
    if (state.range === prev.range && state.customRange === prev.customRange) return;
    writeUrl();
  });
}

/** One-shot URL → store hydration on /history mount. Returns optional cursor anchor. */
export function hydrateHistoryFromUrl(): number | null {
  const params = new URLSearchParams(window.location.search);
  const m  = params.get('machines');
  const p  = params.get('pins');
  const r  = coerceRange(params.get('range'));
  const fm = params.get('from');
  const tm = params.get('to');
  const ts = params.get('ts');

  const v = useViewStore.getState();
  const h = useHistoryStore.getState();
  if (m) v.setMachines(m.split(',').filter(Boolean));
  if (p) v.setLinkedPins(p.split(',').filter(Boolean));
  if (r) h.setRange(r);
  if (fm && tm) {
    const fromMs = Number(fm), toMs = Number(tm);
    if (!isNaN(fromMs) && !isNaN(toMs)) {
      // Same right-edge clamp as the persist migrate — share-link with
      // a stale wider window snaps to 3d ending at the requested toMs.
      const clamped = toMs - fromMs > MAX_HISTORY_WINDOW_MS
        ? { fromMs: toMs - MAX_HISTORY_WINDOW_MS, toMs }
        : { fromMs, toMs };
      h.setCustomRange(clamped);
    }
  }
  return ts && !isNaN(Number(ts)) ? Number(ts) : null;
}
